import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  FiChevronRight,
  FiHome,
  FiX,
  FiImage,
  FiMoreVertical,
  FiChevronLeft,
  FiCheck,
  FiXCircle,
  FiDownload,
} from "react-icons/fi";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  getAllWithdrawals,
  approveWithdrawal,
  resetApproveStatus,
} from "../../store/slices/withdraw";

const WithdrawalRequest = () => {
  const dispatch = useDispatch();

  const [selectedImage, setSelectedImage] = useState(null);
  const [openAction, setOpenAction] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const {
    withdrawals = [],
    withdrawalsLoading,
    withdrawalsError,
    approveLoading,
    approveError,
    pagination = {},
  } = useSelector((state) => state.withdraw);

  const withdrawalCount = pagination?.total || 0;

  useEffect(() => {
    dispatch(
      getAllWithdrawals({
        page,
        limit,
      })
    );
  }, [dispatch, page, limit]);

  const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) {
      return "-";
    }

    return parsedDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };



  const handleWithdrawalAction = async (
    withdrawalId,
    action
  ) => {
    try {
      await dispatch(
        approveWithdrawal({
          withdrawalId,
          action,
        })
      ).unwrap();

      setOpenAction(null);

      dispatch(resetApproveStatus());

      /* Refresh withdrawal list */

      dispatch(
        getAllWithdrawals({
          page: 1,
          limit: 20,
        })
      );
    } catch (error) {
      console.error(
        "Withdrawal action failed:",
        error
      );
    }
  };

  const closeImageViewer = () => {
    setSelectedImage(null);
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloadLoading(true);

      const response = await dispatch(
        getAllWithdrawals({
          page: 1,
          limit: 100000,
        })
      ).unwrap();

      const exportList =
        response?.data?.withdrawals ||
        response?.withdrawals ||
        [];

      if (!exportList.length) {
        alert("No withdrawal records available.");
        return;
      }

      const doc = new jsPDF("landscape", "mm", "a4");

      doc.setFontSize(16);
      doc.text("Withdrawal Request", 14, 15);

      const tableData = exportList.map((item, index) => {
        const user = item?.userId || {};
        const status = item?.status || "PENDING";

        return [
          index + 1,
          user?.name || "-",
          user?.userId || "-",
          user?.walletAddress || "-",
          `$ ${Number(item?.amount || 0).toLocaleString("en-IN", {
            maximumFractionDigits: 2,
          })}`,
          item?.orderId || "-",
          formatDate(item?.requestedAt || item?.createdAt),
          status,
        ];
      });

      autoTable(doc, {
        startY: 22,

        head: [
          [
            "#",
            "Name",
            "User ID",
            "Wallet Address",
            "Amount",
            "Order ID",
            "Date",
            "Status",
          ],
        ],

        body: tableData,

        theme: "grid",

        styles: {
          fontSize: 8,
          cellPadding: 3,
        },

        headStyles: {
          fontSize: 8,
          fontStyle: "bold",
        },
      });

      doc.save("withdrawal-request.pdf");

    } catch (error) {
      console.error("PDF download failed:", error);
      alert("Failed to download PDF.");
    } finally {
      setDownloadLoading(false);
    }
  };

  const totalPages = Math.ceil(withdrawalCount / limit);

  const displayStart =
    withdrawalCount > 0
      ? (page - 1) * limit + 1
      : 0;

  const displayEnd = Math.min(
    page * limit,
    withdrawalCount
  );

  const handlePrevious = () => {
    if (page > 1 && !withdrawalsLoading) {
      setPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages && !withdrawalsLoading) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (
      pageNumber >= 1 &&
      pageNumber <= totalPages &&
      pageNumber !== page &&
      !withdrawalsLoading
    ) {
      setPage(pageNumber);
    }
  };

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          page - 1,
          page,
          page + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  return (
    <div className="gi-referral-page">

      <div className="gi-breadcrumb">

        <Link to="/dashboard">
          <FiHome />
          Dashboard
        </Link>

        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>
          Withdraw
        </span>
        <FiChevronRight className="gi-breadcrumb-arrow" />

        <span>
          Withdraw Request
        </span>

      </div>

      {/* ================= TABLE ================= */}

      <section className="gi-referral-table-card">

        {/* ================= FILTER / ACTION BAR ================= */}

        <div className="gi-users-filter">

          {/* PER PAGE */}

          <div className="gi-users-page-size me-2">
            <label htmlFor="pageSize">
              Show
            </label>

            <select
              id="pageSize"
              value={limit}
              onChange={(e) => {
                const newLimit = Number(e.target.value);

                setLimit(newLimit);
                setPage(1);
              }}
              disabled={withdrawalsLoading}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <span>per page</span>
          </div>

          {/* DOWNLOAD */}

          <button
            type="button"
            className="gi-user-export-btn "
            onClick={handleDownloadPDF}
            disabled={
              downloadLoading ||
              withdrawalsLoading ||
              withdrawals.length === 0
            }
          >
            <FiDownload />

            <span>
              {downloadLoading
                ? "Downloading..."
                : "Download PDF"}
            </span>
          </button>

        </div>

        <div className="gi-referral-table-wrapper">

          <table className="gi-referral-table">

            <thead>

              <tr>

                <th>
                  #
                </th>

                <th>
                  Name
                </th>

                <th className="text-center">
                  User ID
                </th>

                <th className="text-center">
                  Wallet Address
                </th>

                <th className="text-center">
                  Amount
                </th>

                <th className="text-center">
                  Order Id
                </th>

                <th className="text-center">
                  Date
                </th>

                <th className="text-center">
                  Status
                </th>

                <th className="text-center">
                  Action
                </th>

              </tr>

            </thead>

            <tbody>

              {/* ================= LOADING ================= */}

              {withdrawalsLoading && (

                <tr>

                  <td
                    colSpan="9"
                    className="gi-table-message"
                  >
                    Loading payments...
                  </td>

                </tr>

              )}

              {/* ================= ERROR ================= */}

              {!withdrawalsLoading &&
                withdrawalsError && (

                  <tr>

                    <td
                      colSpan="9"
                      className="gi-table-message"
                    >
                      {withdrawalsError}
                    </td>

                  </tr>

                )}

              {/* ================= APPROVE ERROR ================= */}

              {!withdrawalsLoading &&
                !withdrawalsError &&
                approveError && (

                  <tr>

                    <td
                      colSpan="9"
                      className="gi-table-message"
                    >
                      {approveError}
                    </td>

                  </tr>

                )}

              {/* ================= NO DATA ================= */}

              {!withdrawalsLoading &&
                !withdrawalsError &&
                withdrawals.length === 0 && (

                  <tr>

                    <td
                      colSpan="9"
                      className="gi-table-message"
                    >
                      No payments found
                    </td>

                  </tr>

                )}

              {/* ================= WITHDRAWAL DATA ================= */}

              {!withdrawalsLoading &&
                !withdrawalsError &&
                withdrawals.map(
                  (item, index) => {

                    const user =
                      item?.userId || {};

                    const status =
                      item?.status ||
                      "PENDING";

                    return (

                      <tr
                        key={
                          item?._id ||
                          index
                        }
                      >

                        {/* SERIAL */}

                        <td className="gi-referral-number">

                          {String(
                            (page - 1) * limit + index + 1
                          ).padStart(2, "0")}

                        </td>

                        {/* USER */}

                        <td>


                          {user?.name ||
                            "-"}


                        </td>

                        {/* USER ID */}

                        <td className="text-center">

                          <span className="gi-user-id">

                            {user?.userId ||
                              "-"}

                          </span>

                        </td>

                        {/* WALLET ADDRESS */}

                        <td className="text-center">

                          <span className="gi-user-id">

                            {user?.walletAddress ||
                              "-"}

                          </span>

                        </td>

                        {/* AMOUNT */}

                        <td className="text-center">

                          <strong className="gi-payment-amount">

                            ${" "}
                            {Number(
                              item?.amount || 0
                            ).toLocaleString(
                              "en-IN",
                              {
                                maximumFractionDigits: 2,
                              }
                            )}

                          </strong>

                        </td>

                        {/* ORDER ID */}

                        <td className="text-center">

                          <span className="gi-user-id">

                            {item?.orderId ||
                              "-"}

                          </span>

                        </td>

                        {/* DATE */}

                        <td className="text-center">

                          <span className="gi-date-text">

                            {formatDate(
                              item?.requestedAt ||
                              item?.createdAt
                            )}

                          </span>

                        </td>

                        {/* STATUS */}

                        <td className="text-center">

                          <span
                            className={`gi-status-badge ${status.toLowerCase()}`}
                          >



                            {status}

                          </span>

                        </td>

                        {/* ACTION */}

                        <td className="text-center">

                          {status === "PENDING" ? (

                            <div className="gi-payment-action-wrap">

                              <button
                                type="button"
                                className="gi-payment-action-btn"
                                disabled={
                                  approveLoading
                                }
                                onClick={() =>
                                  setOpenAction(
                                    openAction ===
                                      item?._id
                                      ? null
                                      : item?._id
                                  )
                                }
                              >

                                <FiMoreVertical />

                              </button>

                              {openAction ===
                                item?._id && (

                                  <div className="gi-payment-action-menu">

                                    {/* APPROVE */}

                                    <button
                                      type="button"
                                      className="gi-action-approve"
                                      disabled={
                                        approveLoading
                                      }
                                      onClick={() =>
                                        handleWithdrawalAction(
                                          item?._id,
                                          "APPROVE"
                                        )
                                      }
                                    >

                                      <FiCheck />

                                      Approve

                                    </button>

                                    {/* REJECT */}

                                    <button
                                      type="button"
                                      className="gi-action-reject"
                                      disabled={
                                        approveLoading
                                      }
                                      onClick={() =>
                                        handleWithdrawalAction(
                                          item?._id,
                                          "REJECT"
                                        )
                                      }
                                    >

                                      <FiXCircle />

                                      Reject

                                    </button>

                                  </div>

                                )}

                            </div>

                          ) : (

                            <span className="gi-action-completed">

                              Approved

                            </span>

                          )}

                        </td>

                      </tr>

                    );
                  }
                )}

            </tbody>

          </table>
          {withdrawalCount > 0 && (
            <div className="gi-users-pagination">

              {/* PAGINATION INFO */}

              <div className="gi-pagination-info">
                Showing{" "}

                <strong>
                  {displayStart}
                </strong>

                {" "}to{" "}

                <strong>
                  {displayEnd}
                </strong>

                {" "}of{" "}

                <strong>
                  {withdrawalCount}
                </strong>

                {" "}withdrawals
              </div>


              {/* PAGINATION BUTTONS */}

              <div className="gi-pagination-buttons">

                {/* PREVIOUS */}

                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={handlePrevious}
                  disabled={
                    page === 1 ||
                    withdrawalsLoading
                  }
                >
                  <FiChevronLeft />
                </button>


                {/* PAGE NUMBERS */}

                {getPageNumbers().map(
                  (pageNumber, index) => {

                    if (pageNumber === "...") {
                      return (
                        <span
                          key={`dots-${index}`}
                          className="gi-pagination-dots"
                        >
                          ...
                        </span>
                      );
                    }

                    return (
                      <button
                        type="button"
                        key={pageNumber}
                        className={`gi-pagination-number ${page === pageNumber
                          ? "active"
                          : ""
                          }`}
                        onClick={() =>
                          handlePageChange(pageNumber)
                        }
                        disabled={withdrawalsLoading}
                      >
                        {pageNumber}
                      </button>
                    );
                  }
                )}


                {/* NEXT */}

                <button
                  type="button"
                  className="gi-pagination-btn"
                  onClick={handleNext}
                  disabled={
                    page >= totalPages ||
                    withdrawalsLoading
                  }
                >
                  <FiChevronRight />
                </button>

              </div>

            </div>
          )}
        </div>

      </section>

      {/* ================= IMAGE VIEWER ================= */}

      {selectedImage && (

        <div
          className="gi-image-viewer-overlay"
          onClick={closeImageViewer}
        >

          <div
            className="gi-image-viewer-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* CLOSE */}

            <button
              type="button"
              className="gi-image-viewer-close"
              onClick={closeImageViewer}
            >

              <FiX />

            </button>

            {/* HEADER */}

            <div className="gi-image-viewer-header">

              <div className="gi-image-viewer-title">

                <div className="gi-image-viewer-icon">

                  <FiImage />

                </div>

                <div>

                  <h3>
                    Payment Proof
                  </h3>

                  <p>
                    {selectedImage.userName ||
                      "User"}
                  </p>

                </div>

              </div>

            </div>

            {/* IMAGE */}

            <div className="gi-image-viewer-body">

              <img
                src={selectedImage.url}
                alt="Payment Proof"
              />

            </div>

            {/* FOOTER */}

            <div className="gi-image-viewer-footer">

              <span>

                Transaction ID:

                <strong>
                  {selectedImage.transactionId ||
                    "-"}
                </strong>

              </span>

            </div>

          </div>

        </div>

      )}

    </div>
  );
};

export default WithdrawalRequest;